import { useState } from 'react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

interface FAQItem {
  q: string;
  a: string;
}

interface FAQSectionProps {
  items?: FAQItem[];
  heading?: string;
}

const DEFAULT_FAQS: FAQItem[] = [
  {
    q: 'Who provides furnace installation near me in Oakville or Burlington?',
    a: 'Atlantis HVAC Systems Inc. provides expert furnace installation throughout Oakville, Burlington, and Mississauga via our GTA West team at (905) 845-4009. We service all major brands with T.S.S.A.-certified technicians and transparent upfront pricing.',
  },
  {
    q: 'Is there 24-hour emergency HVAC service available in Toronto?',
    a: 'Yes. Atlantis HVAC Systems offers 24/7 emergency heating and cooling service across Toronto, Etobicoke, and the broader GTA. Call our GTA East line at (416) 891-3444 for immediate assistance at any time, day or night.',
  },
  {
    q: 'How much does air conditioner installation cost in the Greater Toronto Area?',
    a: 'HVAC installation costs vary based on unit size, brand, and home configuration. Atlantis provides free in-home consultations with upfront, honest pricing and no hidden fees. Contact us to schedule a no-obligation assessment before any commitment.',
  },
  {
    q: 'How long does a furnace replacement typically take?',
    a: 'A standard furnace replacement takes four to eight hours depending on system type and home configuration. Most Atlantis jobs are completed within a single day. Our technicians work efficiently while fully respecting your home and schedule.',
  },
  {
    q: 'Does Atlantis HVAC Systems offer free consultations?',
    a: 'Yes — we provide complimentary in-home consultations for all new equipment inquiries. A certified technician assesses your system, discusses your comfort goals, and presents transparent recommendations at zero cost and with no obligation.',
  },
  {
    q: 'What areas does Atlantis HVAC Systems serve in Ontario?',
    a: 'We serve Oakville, Burlington, Mississauga, Toronto, Etobicoke, and Waterdown. GTA West (905) 845-4009 covers Oakville, Burlington, and Mississauga. GTA East (416) 891-3444 handles Toronto and Etobicoke.',
  },
  {
    q: 'Is Atlantis HVAC Systems T.S.S.A. certified?',
    a: 'Yes. Atlantis HVAC Systems is fully T.S.S.A.-approved — certified by Ontario\'s Technical Standards and Safety Authority. This means all installations and repairs meet the province\'s strictest safety, code, and quality standards.',
  },
];

export default function FAQSection({ items, heading }: FAQSectionProps) {
  const [open, setOpen] = useState<number | null>(0);
  const { ref, visible } = useScrollReveal<HTMLElement>();
  const faqs = items ?? DEFAULT_FAQS;
  const title = heading ?? 'Frequently Asked Questions';

  return (
    <section ref={ref} className="bg-luxury-warm py-28 lg:py-36" itemScope itemType="https://schema.org/FAQPage">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">

          {/* Left sticky label */}
          <div
            className={`lg:col-span-4 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="lg:sticky lg:top-28">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-0.5 bg-luxury-gold-dark" />
                <span className="font-caps text-[10px] tracking-[0.3em] uppercase text-luxury-text-muted">Voice Search Ready</span>
              </div>
              <h2 className="font-serif text-4xl lg:text-5xl text-luxury-text-dark leading-tight mb-5">
                {title}
              </h2>
              <p className="font-sans text-sm text-luxury-text-muted leading-relaxed mb-8">
                Common questions about HVAC services in the GTA, answered clearly and directly.
              </p>

              {/* Contact box */}
              <div className="bg-white rounded-2xl p-6 border border-luxury-gold/15 shadow-card">
                <p className="font-caps text-[9px] tracking-[0.25em] uppercase text-luxury-gold-dark mb-4">Still have questions?</p>
                <div className="flex flex-col gap-3">
                  <a href="tel:9058454009" className="flex items-center gap-3 group cursor-pointer">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-luxury-warm border border-luxury-gold/22 group-hover:bg-luxury-gold/15 transition-colors flex-shrink-0">
                      <i className="ri-phone-line text-luxury-gold-dark text-sm" />
                    </div>
                    <div>
                      <span className="font-caps text-[9px] tracking-[0.2em] uppercase text-luxury-text-muted block">GTA West</span>
                      <span className="font-caps font-semibold text-xs tracking-wider text-luxury-text-dark group-hover:text-luxury-gold-dark transition-colors whitespace-nowrap">(905) 845-4009</span>
                    </div>
                  </a>
                  <a href="tel:4168913444" className="flex items-center gap-3 group cursor-pointer">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-luxury-warm border border-luxury-gold/22 group-hover:bg-luxury-gold/15 transition-colors flex-shrink-0">
                      <i className="ri-phone-line text-luxury-gold-dark text-sm" />
                    </div>
                    <div>
                      <span className="font-caps text-[9px] tracking-[0.2em] uppercase text-luxury-text-muted block">GTA East</span>
                      <span className="font-caps font-semibold text-xs tracking-wider text-luxury-text-dark group-hover:text-luxury-gold-dark transition-colors whitespace-nowrap">(416) 891-3444</span>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Accordion */}
          <div
            className={`lg:col-span-8 flex flex-col gap-3 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {faqs.map((faq, i) => (
              <div
                key={i}
                className={`bg-white rounded-2xl overflow-hidden border transition-all duration-300 shadow-card ${
                  open === i ? 'border-luxury-gold/35' : 'border-gray-100 hover:border-luxury-gold/22'
                }`}
                itemScope itemProp="mainEntity" itemType="https://schema.org/Question"
              >
                <button
                  onClick={() => setOpen(open === i ? null : i)}
                  className="w-full flex items-center justify-between gap-5 px-7 py-5 text-left cursor-pointer group"
                  aria-expanded={open === i}
                >
                  <span
                    className={`font-sans font-medium text-sm leading-snug transition-colors ${
                      open === i ? 'text-luxury-gold-dark' : 'text-luxury-text-dark group-hover:text-luxury-gold-dark'
                    }`}
                    itemProp="name"
                  >
                    {faq.q}
                  </span>
                  <div
                    className={`w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full border transition-all duration-300 ${
                      open === i
                        ? 'border-luxury-gold/45 bg-luxury-gold/12 rotate-180'
                        : 'border-luxury-gold/22 group-hover:border-luxury-gold/40'
                    }`}
                  >
                    <i className="ri-arrow-down-s-line text-luxury-gold text-base" />
                  </div>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-400 ${open === i ? 'max-h-52 pb-6' : 'max-h-0'}`}
                  itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer"
                >
                  <div className="px-7">
                    <div className="border-t border-luxury-gold/10 pt-4">
                      <p className="font-sans text-sm text-luxury-text-muted leading-relaxed" itemProp="text">
                        {faq.a}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
